(function () {
  'use strict';

  const DEFAULT_DURATION = 1600;
  const STAGGER_MS = 120;

  function easeOutCubic(t) {
    return 1 - Math.pow(1 - t, 3);
  }

  function parseConfig(el) {
    return {
      target: Number(el.dataset.target) || 0,
      decimals: Number(el.dataset.decimals) || 0,
      grouping: el.dataset.grouping === 'true',
      prefix: el.dataset.prefix || '',
      suffix: el.dataset.suffix || '',
      duration: Number(el.dataset.duration) || DEFAULT_DURATION,
    };
  }

  function formatNumber(value, config) {
    const safe = Number.isFinite(value) ? value : 0;
    const formatted = new Intl.NumberFormat(undefined, {
      minimumFractionDigits: config.decimals,
      maximumFractionDigits: config.decimals,
      useGrouping: config.grouping,
    }).format(safe);
    return `${config.prefix}${formatted}${config.suffix}`;
  }

  function setFinalValue(el, config) {
    const formatted = formatNumber(config.target, config);
    el.textContent = formatted;
    el.dataset.animated = 'true';

    const labelEl = el.closest('.animated-stats__item')?.querySelector('.animated-stats__label');
    const labelText = labelEl?.textContent?.trim() || '';
    el.setAttribute('aria-label', labelText ? `${labelText}: ${formatted}` : formatted);
  }

  function resetValues(section) {
    section.classList.remove('is-ready');
    section.querySelectorAll('[data-animated-stat-value]').forEach((el) => {
      delete el.dataset.animated;
      const config = parseConfig(el);
      el.textContent = formatNumber(0, config);
    });
  }

  function animateValue(el, config, delay) {
    if (el.dataset.animated === 'true') return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion || config.target === 0) {
      setFinalValue(el, config);
      return;
    }

    window.setTimeout(() => {
      const start = performance.now();
      const from = 0;
      const to = config.target;

      function frame(now) {
        const progress = Math.min((now - start) / config.duration, 1);
        const eased = easeOutCubic(progress);
        const current = from + (to - from) * eased;
        el.textContent = formatNumber(current, config);

        if (progress < 1) {
          requestAnimationFrame(frame);
        } else {
          setFinalValue(el, config);
        }
      }

      requestAnimationFrame(frame);
    }, delay);
  }

  function initSection(section) {
    if (section.dataset.asInitialized) return;
    section.dataset.asInitialized = 'true';

    if (section._asObserver) {
      section._asObserver.disconnect();
      section._asObserver = null;
    }

    const valueEls = section.querySelectorAll('[data-animated-stat-value]');
    if (!valueEls.length) return;

    const run = () => {
      section.classList.add('is-ready');
      valueEls.forEach((el, index) => {
        const config = parseConfig(el);
        animateValue(el, config, index * STAGGER_MS);
      });
    };

    if (!('IntersectionObserver' in window)) {
      run();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          observer.disconnect();
          section._asObserver = null;
          run();
        });
      },
      { root: null, rootMargin: '0px 0px -8% 0px', threshold: 0.15 },
    );

    section._asObserver = observer;
    observer.observe(section);
  }

  function boot() {
    document.querySelectorAll('[data-animated-stats]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.querySelector('[data-animated-stats]');
    if (!root) return;
    delete root.dataset.asInitialized;
    resetValues(root);
    initSection(root);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.querySelector('[data-animated-stats]');
    if (!root?._asObserver) return;
    root._asObserver.disconnect();
    root._asObserver = null;
  });
})();
